import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Temporal } from 'temporal-polyfill';
import { z } from 'zod';

import { airColumnHelper } from '@/lib/tables';
import { EmptyArray, getSchoolReports, getSchoolReportsCsv } from '@/lib/api-calls';
import { useErrorToast } from '@/lib/error-handling';
import { paginationRequestSchema } from '@/lib/pagination';
import { usePaginatedFilters } from '@/lib/filtering';
import { useNavigateSearchState, validateRouteRole } from '@/lib/routing';
import { getStudentYearOptions } from '@/lib/student-utils';
import { useSchoolOptions } from '@/lib/api-call-hooks';
import { useSorting } from '@/lib/sorting';
import { formatDateRange, formatInstant, FrequentTimeZones, isAirAdmin } from '@airreading/common';
import {
  getSchoolReportsSortableColumnSchema,
  getSchoolReportsSortOrderSchema,
  GetSchoolReportsSortableColumn,
  SchoolReport,
  studentYearOption,
  studentYearNullableSchema,
  SchoolReportsTableColumnIds,
  toSessionFeedbackCompoundId,
} from '@airreading/common/data';

import { InfoPage } from '@/components/info-page';
import { createTableRowSelectionCell, EntityTable } from '@/components/table/entity-table';
import { TableErrorState } from '@/components/table/table-error-state';
import { TableFilterDropdown } from '@/components/table/table-filter-dropdown';
import { TableFilterInputField } from '@/components/table/table-filter-field';
import { TableFilterResetButton } from '@/components/table/table-filter-reset-button';
import { TableFilterPlainDateRangePicker } from '@/components/table/table-filter-plain-date-range-picker';
import { CopyableLink } from '@/components/copyable-link';
import { schoolTabs } from '../-components/tabs';
import { normalizeNewlines } from '@/lib/string-utils';
import { exportToCSV, buildClientExportColumns } from '@/lib/csv-utils';
import { DropdownMenuItem } from '@/components/ui/dropdown-menu';
import { getSchoolReportsTableOverrides } from './-generate-school-reports-overrides';
import { useRowsBulkSelection } from '@/lib/bulk-selection';
import { getSessionFeedbackEvent } from '@/components/feedbacks/feedback-issues';
import { LoaderIcon } from 'lucide-react';

const schoolsSearchSchema = paginationRequestSchema.extend({
  query: z.string().optional(),
  schools: z.string().array().optional(),
  grades: z.array(studentYearNullableSchema).optional(),
  startDate: z.string().optional(),
  endDate: z.string().optional(),
  sortBy: getSchoolReportsSortOrderSchema.optional(),
});

export const Route = createFileRoute('/_layout/orgs/schools/reports/')({
  beforeLoad: validateRouteRole(isAirAdmin),
  component: SchoolReportsIndex,
  validateSearch: schoolsSearchSchema,
});

const columnHelper = airColumnHelper<SchoolReport>(getSchoolReportsSortableColumnSchema);
const buildColumns = (
  isSelectAllPagesActive: boolean,
  onSelectAllPagesChange: (isSelectAllActive: boolean) => void,
) => [
  createTableRowSelectionCell(columnHelper, { isSelectAllPagesActive, onSelectAllPagesChange }),
  columnHelper.accessor((row) => row, {
    id: SchoolReportsTableColumnIds.dateRange,
    header: 'Date range',
    cell: ({ getValue }) => {
      const { startedAtInstant, endedAtInstant } = getValue();
      return formatDateRange(startedAtInstant, endedAtInstant, FrequentTimeZones.UsCentral);
    },
  }),
  columnHelper.accessor('school', {
    id: SchoolReportsTableColumnIds.school,
    header: 'School',
    cell: ({ getValue }) => {
      const school = getValue();
      return <div>{school ? school.name : '-'}</div>;
    },
  }),
  columnHelper.accessor('school', {
    id: SchoolReportsTableColumnIds.district,
    header: 'District',
    cell: ({ getValue }) => {
      const school = getValue();
      return <div>{school ? school.org.name : '-'}</div>;
    },
  }),
  columnHelper.accessor('student', {
    id: SchoolReportsTableColumnIds.classroomTeacher,
    header: 'Classroom teacher',
    cell: ({ getValue }) => {
      const student = getValue();
      return <div>{student?.classroomTeacher ?? '-'}</div>;
    },
  }),
  columnHelper.accessor('report', {
    id: SchoolReportsTableColumnIds.report,
    header: 'Skill report',
    cell: ({ getValue }) => {
      const report = normalizeNewlines(getValue());
      return <div className="whitespace-pre-line">{report}</div>;
    },
  }),
  columnHelper.accessor('grade', {
    id: SchoolReportsTableColumnIds.grade,
    header: 'Grade',
    cell: ({ getValue }) => studentYearOption(getValue()),
  }),
  columnHelper.accessor('student', {
    id: SchoolReportsTableColumnIds.student,
    header: 'Student name (full)',
    cell: ({ getValue }) => {
      const student = getValue();
      return <div>{student ? `${student.fullNameOrEmail}` : '-'}</div>;
    },
  }),
  columnHelper.accessor('student', {
    id: SchoolReportsTableColumnIds.studentFirstName,
    header: 'Student first name',
    cell: ({ getValue }) => {
      const student = getValue();
      return <div>{student?.firstName ?? '-'}</div>;
    },
  }),
  columnHelper.accessor('student', {
    id: SchoolReportsTableColumnIds.studentLastName,
    header: 'Student last name',
    cell: ({ getValue }) => {
      const student = getValue();
      return <div>{student?.lastName ?? '-'}</div>;
    },
  }),
  columnHelper.accessor('reportFromAI', {
    id: SchoolReportsTableColumnIds.reportFromAI,
    header: 'Claude AI skill report',
    cell: ({ getValue }) => {
      const reportFromAI = getValue();
      return <div className="max-w-lg whitespace-normal break-words">{reportFromAI ?? '-'}</div>;
    },
  }),
  columnHelper.accessor('reportFromAISpanish', {
    id: SchoolReportsTableColumnIds.reportFromAISpanish,
    header: 'Spanish Claude AI skill report',
    cell: ({ getValue }) => {
      const reportFromAISpanish = getValue();
      return <div className="max-w-lg whitespace-normal break-words">{reportFromAISpanish ?? '-'}</div>;
    },
  }),
  columnHelper.accessor('floridaStandards', {
    id: SchoolReportsTableColumnIds.floridaStandards,
    header: 'Florida standards',
    cell: ({ getValue }) => {
      const floridaStandards = getValue();
      return <div className="max-w-lg whitespace-normal break-words">{floridaStandards || '-'}</div>;
    },
  }),
  columnHelper.accessor('arkansasStandards', {
    id: SchoolReportsTableColumnIds.arkansasStandards,
    header: 'Arkansas standards',
    cell: ({ getValue }) => {
      const arkansasStandards = getValue();
      return <div className="max-w-lg whitespace-normal break-words">{arkansasStandards || '-'}</div>;
    },
  }),
  columnHelper.accessor((row) => row, {
    id: SchoolReportsTableColumnIds.studentFeedbackEvents,
    header: 'Student feedback events',
    cell: ({ getValue }) => {
      const { sessions, studentId } = getValue();
      return sessions.length > 0 ? (
        <div className="flex flex-wrap max-w-lg whitespace-normal break-words">
          {sessions.map((session, i) => {
            const { formattedDate } = formatInstant(session.startInstant, FrequentTimeZones.UsCentral);
            const feedback = session.sessionFeedbacks?.find((feedback) => feedback.studentId === studentId);
            const event = getSessionFeedbackEvent({
              studentAttended: !!feedback?.studentAttended,
              issues: feedback?.issues ?? [],
            });

            return (
              event && (
                <div className="flex flex-row" key={session.id}>
                  <div>
                    {event.toLowerCase()} [{formattedDate}]
                  </div>
                  {i < sessions.length - 1 && <div className="mr-1">,</div>}
                </div>
              )
            );
          })}
        </div>
      ) : (
        '-'
      );
    },
  }),
  columnHelper.accessor((row) => row, {
    id: SchoolReportsTableColumnIds.sessionAttendance,
    header: 'Session attendance',
    cell: ({ getValue }) => {
      const { sessions, studentId } = getValue();
      return sessions.length > 0 ? (
        <div className="flex flex-wrap max-w-lg">
          {sessions.map((session, i) => {
            const { formattedDate } = formatInstant(session.startInstant, FrequentTimeZones.UsCentral);
            const feedback = session.sessionFeedbacks?.find((feedback) => feedback.studentId === studentId);

            return (
              <div className="flex flex-row" key={session.id}>
                {feedback ? (
                  <CopyableLink
                    to="/sessions/session-feedbacks/$feedbackId"
                    params={{ feedbackId: toSessionFeedbackCompoundId(feedback) }}
                    canCopyUrl={false}
                    showExternalHrefIcon={false}
                  >
                    {formattedDate}
                  </CopyableLink>
                ) : (
                  <div>{formattedDate}</div>
                )}
                {i < sessions.length - 1 && <div className="mr-1">,</div>}
              </div>
            );
          })}
        </div>
      ) : (
        '-'
      );
    },
  }),
];

function SchoolReportsIndex() {
  const [isSelectAllPagesActive, setIsSelectAllPagesActive] = useState(false);
  const [isDownloadingCsv, setIsDownloadingCsv] = useState(false);
  const {
    pageIndex,
    pageSize,
    onPaginationChange,
    filters: {
      query: [[query, activeQuery], setQuery],
      startDate: [[selectedStartDate], setSelectedStartDate],
      endDate: [[selectedEndDate], setSelectedEndDate],
      schools: [[selectedSchools, activeSelectedSchools], setSelectedSchools],
      grades: [[selectedGrades, activeSelectedGrades], setSelectedGrades],
    },
    resetFilters,
  } = usePaginatedFilters(Route.id, ['query', 'startDate', 'endDate', 'schools', 'grades']);
  const [sortBy, didChangeSortBy] = useNavigateSearchState(Route.id, 'sortBy');
  const { sorting, ...tableSortingOptions } = useSorting<GetSchoolReportsSortableColumn>([sortBy, didChangeSortBy]);

  const today = Temporal.Now.plainDateISO(FrequentTimeZones.UsCentral);
  // Always go to last week's Monday
  // dayOfWeek: 1 = Monday, ..., 7 = Sunday
  const daysSinceMonday = today.dayOfWeek - 1;
  const lastMonday = today.subtract({ days: daysSinceMonday + 7 });
  const lastSaturday = lastMonday.add({ days: 5 });

  const {
    data: response,
    error,
    refetch,
    isPending: isLoadingSchoolReports,
  } = useQuery(
    getSchoolReports({
      pageIndex,
      pageSize,
      filters: {
        query: activeQuery,
        startOnOrAfter: (selectedStartDate ? Temporal.PlainDate.from(selectedStartDate) : lastMonday)
          .toZonedDateTime('UTC')
          .startOfDay()
          .toInstant()
          .toString(),
        startOnOrBefore: (selectedEndDate ? Temporal.PlainDate.from(selectedEndDate) : lastSaturday)
          .toZonedDateTime('UTC')
          .add({ days: 1 })
          .startOfDay()
          .subtract({ milliseconds: 1 })
          .toInstant()
          .toString(),
        schools: activeSelectedSchools,
        grades: activeSelectedGrades,
      },
      sortBy,
    }),
  );
  const schoolReports = useMemo(() => response?.schoolReports || EmptyArray, [response]);
  const allSchoolOptions = useSchoolOptions();

  useErrorToast(error, 'Error loading school reports. Please try again.');

  const { rowSelection, rowDataSelection, onRowSelectionChange, getRowId, resetAllRowSelection } =
    useRowsBulkSelection<SchoolReport>({
      getRowId: (row) => row.id,
      rowIdToData: (id) => schoolReports.find((schoolReport) => schoolReport.id === id),
    });

  // Handle isSelectAllPagesActive changes and reset selections when deselecting all
  const handleSelectAllPagesChange = useCallback(
    (isActive: boolean) => {
      setIsSelectAllPagesActive(isActive);
      if (!isActive) {
        // When deselecting all pages, reset all row selections
        resetAllRowSelection();
      }
    },
    [resetAllRowSelection],
  );

  // Select all rows on current page when isSelectAllPagesActive is true and schoolReports change
  useEffect(() => {
    if (isSelectAllPagesActive && schoolReports.length > 0) {
      const allRowIds = schoolReports.reduce(
        (acc, schoolReport) => {
          acc[schoolReport.id] = true;
          return acc;
        },
        {} as Record<string, boolean>,
      );
      onRowSelectionChange(allRowIds);
    }
  }, [schoolReports, isSelectAllPagesActive]);

  // Build columns with select all pages support
  const columns = useMemo(
    () => buildColumns(isSelectAllPagesActive, handleSelectAllPagesChange),
    [isSelectAllPagesActive, handleSelectAllPagesChange],
  );

  // Get row count
  const totalRowCount = useMemo(() => response?.total || 0, [response]);

  // Reset isSelectAllPagesActive when filters change
  useEffect(() => {
    if (isSelectAllPagesActive) {
      setIsSelectAllPagesActive(false);
      resetAllRowSelection();
    }
  }, [activeQuery, activeSelectedSchools, activeSelectedGrades, selectedStartDate, selectedEndDate]);

  const table = useReactTable({
    data: schoolReports,
    columns,
    state: {
      pagination: {
        pageIndex,
        pageSize,
      },
      rowSelection,
      sorting,
    },
    onRowSelectionChange,
    enableRowSelection: true,
    getRowId,
    getCoreRowModel: getCoreRowModel(),
    onPaginationChange,
    manualPagination: true,
    rowCount: response?.total || 0,
    ...tableSortingOptions,
  });

  const filterLeftComponents = useMemo(
    () => (
      <>
        <TableFilterInputField
          placeholder="Search school names"
          value={query ?? ''}
          onChange={(event) => {
            setQuery(event.target.value);
          }}
        />
        <TableFilterDropdown
          title="School"
          options={allSchoolOptions}
          selectedValues={selectedSchools ?? []}
          onChange={setSelectedSchools}
        />
        <TableFilterDropdown
          title="Grade"
          options={getStudentYearOptions()}
          selectedValues={selectedGrades ?? []}
          onChange={setSelectedGrades}
        />
        <TableFilterPlainDateRangePicker
          fromPlainDateString={selectedStartDate}
          toPlainDateString={selectedEndDate}
          onRangeSelect={(range) => {
            setSelectedStartDate(range.fromPlainDateString);
            setSelectedEndDate(range.toPlainDateString);
          }}
          placeholder="Date range"
        />
        <TableFilterResetButton show onClick={resetFilters} />
      </>
    ),
    [
      query,
      allSchoolOptions,
      selectedSchools,
      setSelectedSchools,
      selectedGrades,
      setSelectedGrades,
      selectedStartDate,
      selectedEndDate,
      resetFilters,
      setQuery,
      setSelectedStartDate,
      setSelectedEndDate,
    ],
  );

  const exportColumns = useMemo(() => buildClientExportColumns(columns, getSchoolReportsTableOverrides()), [columns]);

  const handleDownloadCsv = useCallback(
    async (e: Event) => {
      e.preventDefault();
      if (isSelectAllPagesActive) {
        setIsDownloadingCsv(true);
        // When selecting all pages, call backend endpoint to generate CSV with all filtered data
        await getSchoolReportsCsv({
          filters: {
            query: activeQuery,
            startOnOrAfter: (selectedStartDate ? Temporal.PlainDate.from(selectedStartDate) : lastMonday)
              .toZonedDateTime(FrequentTimeZones.UTC)
              .startOfDay()
              .toInstant()
              .toString(),
            startOnOrBefore: (selectedEndDate ? Temporal.PlainDate.from(selectedEndDate) : lastSaturday)
              .toZonedDateTime(FrequentTimeZones.UTC)
              .add({ days: 1 })
              .startOfDay()
              .subtract({ milliseconds: 1 })
              .toInstant()
              .toString(),
            schools: activeSelectedSchools,
            grades: activeSelectedGrades,
          },
          sortBy,
          baseUrl: `${window.location.origin}/sessions/session-feedbacks`,
        });
        setIsDownloadingCsv(false);
      } else {
        // When selecting specific rows, use client-side CSV generation
        const date = Temporal.Now.plainDateISO().toString();
        exportToCSV(rowDataSelection, exportColumns, `school_reports_export_${date}.csv`);
      }
    },
    [
      isSelectAllPagesActive,
      activeQuery,
      selectedStartDate,
      selectedEndDate,
      activeSelectedSchools,
      activeSelectedGrades,
      sortBy,
      lastMonday,
      lastSaturday,
      rowDataSelection,
      exportColumns,
    ],
  );

  const bulkActions = useMemo(
    () => (
      <DropdownMenuItem onSelect={handleDownloadCsv} disabled={isDownloadingCsv}>
        Download as CSV {isDownloadingCsv ? <LoaderIcon className="h-4 w-4 animate-spin" /> : ''}
      </DropdownMenuItem>
    ),
    [handleDownloadCsv, isDownloadingCsv],
  );

  return (
    <InfoPage headerText="Schools" tabs={schoolTabs()}>
      <EntityTable
        table={table}
        hasError={!!error}
        showPagination
        isLoading={isLoadingSchoolReports}
        errorState={<TableErrorState refetchAction={refetch} />}
        filters={{ filterLeftComponents }}
        bulkActionsMenuItems={bulkActions}
        isSelectAllPagesActive={isSelectAllPagesActive}
        onSelectAllPagesChange={handleSelectAllPagesChange}
        totalRowCount={totalRowCount}
      />
    </InfoPage>
  );
}
